import { bindable, customElement, IEventAggregator, resolve, type IDisposable } from 'aurelia';
import { I18N, Signals } from '@aurelia/i18n';
import template from './media-lightbox.html';
import { ConfirmDialog } from './confirm-dialog';
import {
  addMediaItemsToCollection,
  calculateAgeDetailsAtCapture,
  createPersonMarker,
  detectFaces,
  deleteMediaItem,
  deletePersonMarker,
  foldMediaItems,
  formatFileSize,
  formatPersonName,
  getMediaDownloadUrl,
  getMediaFoldGroup,
  getMediaMediumPreviewUrl,
  listPersonMarkers,
  removeMediaItemsFromCollection,
  suggestPersonsForRect,
  unfoldMediaGroup,
  unfoldMediaItem,
  updateMediaItem,
  updatePersonMarker,
  upsertUserSetting,
} from '../services/api.service';
import { MediaType, type AgeDisplayMode, type Collection, type CollectionRef, type MarkerTooltipFieldOptions, type MediaFoldGroup, type MediaItem, type Person, type PersonMarker, type PersonSuggestion, type Tag } from '../services/api.types';
import { getAgeAutoThresholdYears } from '../services/person-age-settings.service';
import {
  getFaceMarginBottomPercent,
  getFaceMarginSidePercent,
  getFaceMarginTopPercent,
  getFaceMaxWorkingDimension,
  getFaceScoreThreshold,
  getFaceZoomThresholdPx,
  setFaceZoomThresholdPx,
} from '../services/face-detection-settings.service';
import { formatDate } from '../services/date-format.service';
import {
  getMediaDisplayQuality,
  MEDIA_DISPLAY_QUALITY_CHANGED_CHANNEL,
  type MediaDisplayQuality,
} from '../services/media-display-quality.service';
import { facePreviewSurfaceStyle } from '../services/face-crop.util';
import {
  displayPointToOriginal,
  normalizeRotationDegrees,
  rotationStyle,
} from '../services/media-rotation.util';

const MARKERS_VISIBLE_KEY = 'radish-markers-visible';
const TOOLTIP_FIELDS_KEY = 'radish-marker-tooltip-fields';
const TOOLTIP_FIELDS_VERSION = 2;
const OVERLAY_INFO_KEY = 'radish-lightbox-overlay-info';
const DETAIL_TAB_KEY = 'radish-lightbox-detail-tab';
const DETAIL_TAB_RETURN_KEY = 'radish-lightbox-detail-tab-return';
const handledLightboxKeyEvents = new WeakSet<KeyboardEvent>();
let activeLightbox: MediaLightbox | null = null;

type DetailTab = 'metadata' | 'details' | 'markers';
type NonMarkerDetailTab = Exclude<DetailTab, 'markers'>;

type OverlayInfoOptions = {
  date: boolean;
  time: boolean;
  position: boolean;
  personMarkers: boolean;
};
const MIN_MARKER_SIZE = 0.01; // normalized (1% of image dimension) — ignore accidental clicks

type DrawPoint = { x: number; y: number };
type FaceRect = { x: number; y: number; width: number; height: number };
type ResizeHandle = 'nw' | 'ne' | 'sw' | 'se';
export type FoldChangeOperation = 'representative' | 'item' | 'group';
interface PendingFace extends FaceRect {
  clientId: string;
  confidence: number;
  suggestions: PersonSuggestion[];
}

type MediaEditState = {
  title: string;
  description: string;
  capturedAt: string;
  notAccurateTimestamp: boolean;
  tagNames: string[];
  rotationDegrees: number;
};

@customElement({ name: 'media-lightbox', template })
export class MediaLightbox {
  private readonly i18n: I18N = resolve(I18N);
  private readonly ea: IEventAggregator = resolve(IEventAggregator);
  private _mediaDisplayQualitySub: IDisposable | null = null;
  private _localeSub: IDisposable | null = null;

  @bindable public items: MediaItem[] = [];
  @bindable public startIndex = 0;
  @bindable public closed!: (currentItem: MediaItem | null) => void;
  @bindable public saved!: (item: MediaItem) => void;
  @bindable public deleted!: (id: string) => void;
  @bindable public personDataChanged?: () => void;
  /** Fired after media is successfully added to a collection so filtered hosts can refresh. */
  @bindable public collectionItemsAdded?: (count: number) => void | Promise<void>;
  /** When the lightbox was opened from a specific collection's view, this is that
   *  collection's id — enables the "Remove from this collection" button and provides
   *  the context for it. Null/undefined when opened from the plain gallery. */
  @bindable public currentCollectionId: string | null = null;
  /** Fired when the user removes the currently open item from `currentCollectionId`.
   *  Only the association is removed — the media item itself is never deleted. */
  @bindable public removedFromCollection?: (id: string) => void;
  /** Fired after fold-group management changes so the host can refresh its list and metadata. */
  @bindable public foldChanged?: (
    group: MediaFoldGroup | null,
    updatedItem: MediaItem | null,
    operation: FoldChangeOperation,
  ) => void | Promise<void>;
  /** Whether the host view has additional, not-yet-loaded items beyond the current
   *  `items` array (mirrors the host's infinite-scroll `hasMore`). When true, reaching
   *  the last loaded item still allows navigating further — `loadMore` is invoked first. */
  @bindable public hasMore = false;
  /** Callback that fetches the next page into the host's backing array (which flows back
   *  down through the `items` binding) — same pagination the host's infinite scroll uses. */
  @bindable public loadMore?: () => Promise<void>;
  /** Total number of items in the host's current filtered selection (not just the
   *  currently loaded page) — used by the fullscreen "position in current selection"
   *  overlay, e.g. "43 / 153". */
  @bindable public totalCount = 0;
  /** Folded siblings are loaded as metadata on demand; only the current sibling's full file is
   * requested by the existing media element. */
  public foldedItems: MediaItem[] = [];
  public foldedIndex = 0;
  public foldedLoading = false;
  public showFoldRepresentativeModal = false;
  public foldManagementLoading = false;

  public navLoading = false;

  // Distraction-free full-window view: only the media, the nav arrows and the
  // close/exit control remain visible; the metadata sidebar/toolbar is hidden.
  public isFullscreen = false;

  // Fullscreen "info overlay" — a small opaque menu (bottom-right, like the nav
  // arrows) letting the user toggle which bits of info are shown above the media
  // while browsing distraction-free. Persisted across sessions like the other
  // display-preference toggles in this component.
  public showOverlayMenu = false;
  public overlayInfo: OverlayInfoOptions = this._loadOverlayInfo();
  public activeDetailTab: DetailTab = this._loadDetailTab();
  private _detailTabBeforeMarkers: NonMarkerDetailTab = this._loadDetailTabBeforeMarkers();

  public currentIndex = 0;
  public currentItem: MediaItem | null = null;
  public _locale = '';
  public mediaDisplayQuality: MediaDisplayQuality = getMediaDisplayQuality();
  public displayUrl = '#';
  public title = '';
  public description = '';
  public capturedAt = '';
  public notAccurateTimestamp = false;
  public tags: Tag[] = [];
  public error = '';
  public saving = false;
  public deleting = false;
  public hasUnsavedChanges = false;

  // Bound via `ref` from the template so the previously displayed media can be
  // cleared imperatively (synchronously) before the new item's src is applied.
  // Aurelia batches DOM property writes (like `src.bind`) to the next animation
  // frame, which otherwise lets the old, already-decoded image/video stay
  // visible for a frame after the dialog for a new item becomes visible.
  public imgEl: HTMLImageElement | null = null;
  public videoEl: HTMLVideoElement | null = null;

  // Tracks whether the currently displayed <img> has finished loading, so the
  // person-marker overlay (positioned as percentages of the image box) isn't
  // painted over the still-black/placeholder area before pixels are ready.
  public imageLoaded = false;

  // Focus target so keyboard shortcuts reach this component's window-level capture listener
  // instead of a hidden gallery tile behind the lightbox.
  public lightboxEl: HTMLElement | null = null;

  // Face-marker (person tagging) state — only relevant for photos.
  public imageWrapEl: HTMLElement | null = null;
  public viewerEl: HTMLElement | null = null;
  public markerEditMode = false;
  public markersVisible = localStorage.getItem(MARKERS_VISIBLE_KEY) !== 'false';
  public markers: PersonMarker[] = [];
  public markersLoading = false;
  public isDrawing = false;
  public drawRect: { left: number; top: number; width: number; height: number } | null = null;
  public showPersonPicker = false;
  public hoveredMarker: PersonMarker | null = null;
  public showTooltipSettings = false;
  public tooltipFields: MarkerTooltipFieldOptions = this._loadTooltipFields();
  public detectingFaces = false;
  public pendingFaces: PendingFace[] = [];
  public rotationDegrees = 0;
  public imageWrapStyle = '';
  public imageSurfaceStyle = '';
  public videoStyle = '';
  public selectedPendingFaceId: string | null = null;
  public pendingFacesListEl: HTMLElement | null = null;

  private _drawStart: DrawPoint | null = null;
  private _pendingRect: { x: number; y: number; width: number; height: number } | null = null;
  private _reassignMarker: PersonMarker | null = null;
  private _pendingFaceForPicker: PendingFace | null = null;

  // Move/resize dragging of the currently *selected* pending face (see
  // onPendingFacePointerDown/onFaceResizeHandlePointerDown and friends below), so the user can
  // nudge/resize an auto-detected box to better fit the face before assigning a person.
  // Uses the Pointer Events API (not separate mouse/touch handlers) with explicit pointer
  // capture, because Safari/iPadOS's touch-event translation for the Apple Pencil is unreliable
  // for this kind of small-target drag — Pointer Events handle mouse, touch and pen uniformly and
  // `setPointerCapture` keeps delivering move/up events to the same element even once the pencil
  // tip drifts outside the (often tiny) marker/handle hit area mid-drag.
  private _dragFaceId: string | null = null;
  private _dragMode: 'move' | 'resize' | null = null;
  private _dragHandle: ResizeHandle | null = null;
  private _dragStartPoint: DrawPoint | null = null;
  private _dragStartRect: FaceRect | null = null;
  private _dragPointerId: number | null = null;
  private _dragCaptureEl: HTMLElement | null = null;

  private _keyHandler: ((event: KeyboardEvent) => void) | null = null;
  private _horizontalItemId: string | null = null;
  private _foldedLoadGeneration = 0;
  private _mediumFallbackItemId: string | null = null;
  private _editBaseline: MediaEditState | null = null;
  private _syncingFields = false;
  private _leaveInProgress = false;
  private _resizeObserver: ResizeObserver | null = null;

  public binding(): void {
    // Runs every time this component is (re)activated, including when Aurelia's `if`
    // template controller reactivates a cached view instance. Doing the sync here
    // (before the view is attached/painted) avoids a flash of the previously
    // displayed item that would otherwise briefly show if this ran in `attached()`.
    this._clearMedia();
    this._locale = this.i18n.getLocale();
    this.currentIndex = this.startIndex;
    this.foldedItems = [];
    this.foldedIndex = 0;
    this.showFoldRepresentativeModal = false;
    this.foldManagementLoading = false;
    this.syncFromCurrent();
    this.isFullscreen = false;
    this.focusLightbox();
  }

  public attached(): void {
    // Defensive: if `attached()` were ever invoked twice without a matching `detached()` in
    // between (e.g. an edge case in how the host's `if` template controller reactivates a
    // cached view), remove the PREVIOUS activation's listener (captured before it's overwritten
    // below) so it can never keep reacting to key events alongside the new one — leaving two
    // live listeners would make a single physical key press advance the index twice.
    if (this._keyHandler) window.removeEventListener('keydown', this._keyHandler, true);
    activeLightbox = this;
    this._localeSub?.dispose();
    this._localeSub = this.ea.subscribe(Signals.I18N_EA_CHANNEL, () => {
      this._locale = this.i18n.getLocale();
    });
    this._mediaDisplayQualitySub = this.ea.subscribe(
      MEDIA_DISPLAY_QUALITY_CHANGED_CHANNEL,
      (quality: MediaDisplayQuality) => {
        this.mediaDisplayQuality = quality;
        this._mediumFallbackItemId = null;
        this.imageLoaded = false;
        this._clearMedia();
        this._updateDisplayUrl();
      },
    );
    this._keyHandler = (event: KeyboardEvent) => {
      if (activeLightbox !== this || !this.currentItem) return;

      // Guard against duplicate lightbox listeners processing the same physical key event.
      if (handledLightboxKeyEvents.has(event)) return;
      handledLightboxKeyEvents.add(event);

      if (event.key === 'Escape') {
        event.preventDefault();
        event.stopPropagation();
        if (this.showFoldRepresentativeModal) {
          this.closeFoldRepresentativeModal();
          return;
        }
        if (this.isFullscreen) this.exitFullscreen();
        else void this.close();
        return;
      }

      const target = event.target;
      const isFormControl = target instanceof HTMLElement
        && !!this.lightboxEl?.contains(target)
        && (
        target.tagName === 'INPUT'
        || target.tagName === 'TEXTAREA'
        || target.tagName === 'SELECT'
        || target.isContentEditable
        );
      if (isFormControl) return;

      if (this.showFoldRepresentativeModal) return;
      // Call the same navigation methods the buttons themselves invoke via `click.trigger`,
      // rather than simulating a click on the button DOM element. The button's `disabled`
      // attribute is an Aurelia-bound DOM property that updates asynchronously (batched to the
      // next flush/animation frame) — fast keyboard presses or key-repeat can fire before that
      // attribute reflects the latest canGoPrev/canGoNext state, silently blocking navigation
      // even though the underlying state already allows it. The methods below guard themselves
      // against their own live getters, so calling them directly can never go stale.
      switch (event.key) {
        case 'ArrowLeft':
          event.preventDefault();
          event.stopImmediatePropagation();
          this.prev();
          break;
        case 'ArrowRight':
          event.preventDefault();
          event.stopImmediatePropagation();
          void this.next();
          break;
        case 'ArrowUp':
          event.preventDefault();
          event.stopImmediatePropagation();
          this.prevFolded();
          break;
        case 'ArrowDown':
          event.preventDefault();
          event.stopImmediatePropagation();
          this.nextFolded();
          break;
        case 'f':
        case 'F':
          event.preventDefault();
          event.stopImmediatePropagation();
          this.toggleFullscreen();
          break;
      }
    };
    this._resizeObserver = new ResizeObserver(() => this.updateImageGeometry());
    if (this.viewerEl) this._resizeObserver.observe(this.viewerEl);
    // Capture the event before an underlying gallery tile can handle the same
    // arrow key while it retains focus behind the lightbox.
    window.addEventListener('keydown', this._keyHandler, true);
    this.focusLightbox();
  }

  public detached(): void {
    this._mediaDisplayQualitySub?.dispose();
    this._mediaDisplayQualitySub = null;
    this._localeSub?.dispose();
    this._localeSub = null;
    if (this._keyHandler) window.removeEventListener('keydown', this._keyHandler, true);
    this._keyHandler = null;
    this._resizeObserver?.disconnect();
    this._resizeObserver = null;
    if (activeLightbox === this) activeLightbox = null;
    document.removeEventListener('mousemove', this._onDocumentMouseMove);
    document.removeEventListener('mouseup', this._onDocumentMouseUp);
    document.removeEventListener('touchmove', this._onDocumentTouchMove);
    document.removeEventListener('touchend', this._onDocumentTouchEnd);
    document.removeEventListener('touchcancel', this._onDocumentTouchEnd);
    this._endFaceDrag();
  }

  public startIndexChanged(): void {
    this._clearMedia();
    this.currentIndex = this.startIndex;
    this.syncFromCurrent();
  }

  public itemsChanged(newItems: MediaItem[]): void {
    if (!this.currentItem) return;
    if (!newItems || newItems.length === 0) {
      this.currentItem = null;
      return;
    }

    const currentItemId = this._horizontalItemId ?? this.currentItem.id;
    const nextIndex = newItems.findIndex(item => item.id === currentItemId);
    if (nextIndex >= 0) {
      this.currentIndex = nextIndex;
      const nextItem = newItems[nextIndex];
      if (!this.isFoldedChild && nextItem !== this.currentItem) {
        const foldedCountChanged = nextItem.foldedImageCount !== this.currentItem.foldedImageCount;
        this.currentItem = nextItem;
        if (!this.hasUnsavedChanges) this.syncFields(nextItem);
        if (foldedCountChanged) {
          this._foldedLoadGeneration++;
          this.foldedItems = [];
          this.foldedIndex = 0;
        }
        if (nextItem.foldedImageCount > 0 || nextItem.foldedIntoMediaItemId != null)
          void this._loadFoldedGroup(nextItem.id);
      }
      return;
    }

    // A folded child can be displayed even though the host's representative-only
    // navigation list does not contain that child (and a marker-scoped list may
    // not contain the newly selected representative). Keep the current image in
    // place rather than jumping to an unrelated item when the host refreshes.
    if (this.isFoldedChild) {
      const currentItemIndex = newItems.findIndex(item => item.id === this.currentItem?.id);
      if (currentItemIndex >= 0)
        this.currentIndex = currentItemIndex;
      else
        this.currentIndex = Math.min(this.currentIndex, newItems.length - 1);
      return;
    }

    if (newItems.length === 0) {
      this.currentItem = null;
      return;
    }

    this.currentIndex = Math.min(this.currentIndex, newItems.length - 1);
    this._clearMedia();
    this.syncFromCurrent(false);
  }

  public get isVideo(): boolean {
    return this.currentItem?.mediaType === MediaType.Video;
  }

  public get displayedDetailTab(): DetailTab {
    return this.activeDetailTab === 'markers' && this.isVideo ? 'metadata' : this.activeDetailTab;
  }

  public setDetailTab(tab: DetailTab): void {
    if (tab === 'markers' && this.isVideo) return;
    if (tab !== 'markers') {
      this._detailTabBeforeMarkers = tab;
      localStorage.setItem(DETAIL_TAB_RETURN_KEY, tab);
    }
    this.activeDetailTab = tab;
    localStorage.setItem(DETAIL_TAB_KEY, tab);
  }

  public get canNavigate(): boolean {
    return this.items.length > 1 || this.hasMore;
  }

  public get canGoPrev(): boolean {
    return this.currentIndex > 0;
  }

  public get canGoNext(): boolean {
    return this.currentIndex < this.items.length - 1 || (this.hasMore && !this.navLoading);
  }

  public get isFoldedChild(): boolean {
    return this.currentItem?.foldedIntoMediaItemId != null;
  }

  public get hasFoldedGroup(): boolean {
    return this.foldedItems.length > 1
      && this.currentItem != null
      && this.foldedItems.some(item => item.id === this.currentItem!.id);
  }

  public get canGoFoldedPrev(): boolean {
    return this.hasFoldedGroup && this.foldedIndex > 0;
  }

  public get canGoFoldedNext(): boolean {
    return this.hasFoldedGroup && this.foldedIndex < this.foldedItems.length - 1;
  }

  public get foldedPositionLabel(): string {
    return `${this.foldedIndex + 1} / ${this.foldedItems.length}`;
  }

  public get downloadUrl(): string {
    return this.currentItem ? getMediaDownloadUrl(this.currentItem.id) : '#';
  }

  public downloadCurrent(): void {
    if (!this.currentItem) return;
    // Navigate explicitly instead of using an SPA-intercepted anchor. The BFF
    // response supplies Content-Disposition so the browser downloads the original
    // file without loading the entire image or video into memory.
    window.location.assign(this.downloadUrl);
  }

  // The rectangle to show/re-suggest against in the person-picker's split view — whichever of the
  // three "picking a person for X" flows is currently active (reassigning an existing marker,
  // choosing a person for a just-drawn rect, or for an auto-detected pending face).
  public get personPickerRect(): { x: number; y: number; width: number; height: number } | null {
    if (this._reassignMarker) return this._reassignMarker;
    if (this._pendingFaceForPicker) return this._pendingFaceForPicker;
    if (this._pendingRect) return this._pendingRect;
    return null;
  }

  // Reuse the sidebar's already-computed suggestions for a pending (auto-detected) face instead
  // of letting person-picker re-fetch its own via `SuggestPersonsForRect` — that RPC has no
  // knowledge of the margin-expansion applied to `_pendingFaceForPicker`'s rect and would compute
  // the embedding from a visibly different crop than the one `DetectFaces` originally used,
  // silently producing different (often worse) suggestions for the exact same face. `null` here
  // means "no precomputed suggestions available" (manual/reassign flows), letting person-picker
  // fall back to its own fetch as before.
  public get personPickerInitialSuggestions(): PersonSuggestion[] | null {
    return this._pendingFaceForPicker ? this._pendingFaceForPicker.suggestions : null;
  }

  public get metadataFileSize(): string {
    return formatFileSize(this.currentItem?.fileSizeBytes);
  }

  public get dateTimeInputLocale(): 'de' | 'en-US' {
    return this._locale.toLowerCase().startsWith('de') ? 'de' : 'en-US';
  }

  public get dimensionsLabel(): string {
    if (!this.currentItem?.widthPx || !this.currentItem?.heightPx) return '—';
    return `${this.currentItem.widthPx} × ${this.currentItem.heightPx}px`;
  }

  public get durationLabel(): string {
    const seconds = this.currentItem?.durationSeconds;
    if (seconds == null) return '—';
    const minutes = Math.floor(seconds / 60);
    const restSeconds = Math.round(seconds % 60).toString().padStart(2, '0');
    return `${minutes}:${restSeconds}`;
  }

  public get geoLocationLabel(): string {
    const lat = this.currentItem?.latitude;
    const lon = this.currentItem?.longitude;
    if (lat == null || lon == null) return '';
    return `${lat.toFixed(5)}, ${lon.toFixed(5)}`;
  }

  // "N / total" position of the currently displayed item within the host's full
  // filtered selection (not just the currently loaded page of `items`) — items are
  // always loaded from the start of that selection, so currentIndex maps directly.
  public get positionLabel(): string {
    return `${this.currentIndex + 1} / ${this.totalCount}`;
  }

  public get overlayDateLabel(): string {
    return formatDate(this.currentItem?.capturedAt);
  }

  public get overlayTimeLabel(): string {
    if (!this.currentItem?.capturedAt) return '';
    const d = new Date(this.currentItem.capturedAt);
    if (isNaN(d.getTime())) return '';
    return new Intl.DateTimeFormat(undefined, { hour: '2-digit', minute: '2-digit', second: '2-digit' }).format(d);
  }

  public get hasOverlayInfo(): boolean {
    return this.overlayInfo.date || this.overlayInfo.time || this.overlayInfo.position;
  }

  // Person markers are hidden by default in fullscreen (`!isFullscreen` gate) since
  // that mode is meant to be distraction-free; the fullscreen info menu's dedicated
  // "person markers" toggle re-enables them there, independent of the sidebar's
  // markersVisible toggle (which is unreachable while the sidebar is hidden).
  public get markersOverlayVisible(): boolean {
    if (!this.imageLoaded) return false;
    return this.isFullscreen ? this.overlayInfo.personMarkers : this.markersVisible;
  }

  public get faceZoomThresholdPx(): number {
    return getFaceZoomThresholdPx();
  }

  public set faceZoomThresholdPx(value: number) {
    setFaceZoomThresholdPx(Number(value));
    void upsertUserSetting('faceZoomThresholdPx', String(getFaceZoomThresholdPx())).catch(() => {});
  }

  public get pendingFacesHeading(): string {
    return this.i18n.tr('persons.marker.pendingFacesHeading', { count: this.pendingFaces.length });
  }

  public get detectFacesButtonTitle(): string {
    return this.detectingFaces
      ? this.i18n.tr('persons.marker.detectFacesRunning')
      : this.i18n.tr('persons.marker.detectFacesButton');
  }

  public onImageLoad(): void {
    this.imageLoaded = true;
    this.updateImageGeometry();
    requestAnimationFrame(() => this.updateImageGeometry());
  }

  public onImageError(): void {
    if (!this.currentItem || this.mediaDisplayQuality !== 'medium'
      || this._mediumFallbackItemId === this.currentItem.id) {
      this.imageLoaded = false;
      return;
    }

    this._mediumFallbackItemId = this.currentItem.id;
    this.imageLoaded = false;
    this._updateDisplayUrl();
  }

  public titleChanged(): void {
    this.refreshUnsavedChanges();
  }

  public descriptionChanged(): void {
    this.refreshUnsavedChanges();
  }

  public capturedAtChanged(_value?: string): void {
    this.refreshUnsavedChanges();
  }

  public notAccurateTimestampChanged(): void {
    this.refreshUnsavedChanges();
  }

  public tagsChanged(): void {
    this.refreshUnsavedChanges();
  }

  public tagInputChanged(tags: Tag[]): void {
    this.tags = tags;
    this.refreshUnsavedChanges(tags);
  }

  public async close(): Promise<void> {
    await this.runWithUnsavedChangesGuard(() => {
      if (activeLightbox === this) activeLightbox = null;
      this.closed(this.currentItem);
    });
  }

  public toggleFullscreen(): void {
    this.isFullscreen = !this.isFullscreen;
    this.showOverlayMenu = false;
    requestAnimationFrame(() => this.updateImageGeometry());
  }

  public exitFullscreen(): void {
    this.isFullscreen = false;
    this.showOverlayMenu = false;
    requestAnimationFrame(() => this.updateImageGeometry());
  }
  // The close (✕) button doubles as the fullscreen-exit control while in
  // fullscreen mode, per the "only nav arrows + close" minimal chrome request.
  public async onCloseClick(): Promise<void> {
    if (this.isFullscreen) this.exitFullscreen();
    else await this.close();
  }

  public async prev(): Promise<void> {
    if (!this.canGoPrev) return;
    await this.runWithUnsavedChangesGuard(() => {
      if (!this.canGoPrev) return;
      this._clearMedia();
      this.currentIndex -= 1;
      this.syncFromCurrent(false);
    });
  }

  public async prevFolded(): Promise<void> {
    if (!this.canGoFoldedPrev) return;
    await this.runWithUnsavedChangesGuard(() => {
      if (this.canGoFoldedPrev) this.showFoldedItem(this.foldedIndex - 1);
    });
  }

  public async nextFolded(): Promise<void> {
    if (!this.canGoFoldedNext) return;
    await this.runWithUnsavedChangesGuard(() => {
      if (this.canGoFoldedNext) this.showFoldedItem(this.foldedIndex + 1);
    });
  }

  public openFoldRepresentativeModal(): void {
    if (!this.hasFoldedGroup || this.foldManagementLoading) return;
    this.showFoldRepresentativeModal = true;
  }

  public closeFoldRepresentativeModal(): void {
    if (this.foldManagementLoading) return;
    this.showFoldRepresentativeModal = false;
  }

  public async changeRepresentative(representativeId: string): Promise<void> {
    if (!this.hasFoldedGroup || this.foldManagementLoading) return;

    const requestedItemId = this.currentItem?.id;
    if (!requestedItemId) return;
    this.foldManagementLoading = true;
    this.error = '';
    try {
      const group = await foldMediaItems(
        this.foldedItems.map(item => item.id),
        representativeId,
      );
      if (this.currentItem?.id === requestedItemId)
        this.applyFoldGroup(group);
      this.showFoldRepresentativeModal = false;
      await this.foldChanged?.(group, null, 'representative');
    } catch (error) {
      this.error = error instanceof Error ? error.message : String(error);
      await ConfirmDialog.alert(this.error, this.i18n.tr('gallery.folding.manageTitle'));
    } finally {
      this.foldManagementLoading = false;
    }
  }

  public async unfoldCurrentFolded(): Promise<void> {
    if (!this.currentItem || !this.isFoldedChild || this.foldManagementLoading) return;

    const confirmed = await ConfirmDialog.confirm(
      this.i18n.tr('gallery.folding.unfoldPhotoConfirm'),
      this.i18n.tr('gallery.folding.unfoldPhoto'),
    );
    if (!confirmed) return;

    const requestedItemId = this.currentItem.id;
    this.foldManagementLoading = true;
    this.error = '';
    const group = this.foldGroupSnapshot();
    try {
      const updated = await unfoldMediaItem(requestedItemId);
      if (this.currentItem?.id === requestedItemId)
        this.showStandaloneItem(updated);
      await this.foldChanged?.(group, updated, 'item');
    } catch (error) {
      this.error = error instanceof Error ? error.message : String(error);
    } finally {
      this.foldManagementLoading = false;
    }
  }

  public async unfoldCurrentGroup(): Promise<void> {
    if (!this.currentItem || !this.hasFoldedGroup || this.foldManagementLoading) return;

    const confirmed = await ConfirmDialog.confirm(
      this.i18n.tr('gallery.folding.unfoldGroupConfirm'),
      this.i18n.tr('gallery.folding.unfoldGroup'),
    );
    if (!confirmed) return;

    const requestedItemId = this.currentItem.id;
    this.foldManagementLoading = true;
    this.error = '';
    const group = this.foldGroupSnapshot();
    try {
      const updated = await unfoldMediaGroup(requestedItemId);
      if (this.currentItem?.id === requestedItemId)
        this.showStandaloneItem(updated);
      await this.foldChanged?.(group, updated, 'group');
    } catch (error) {
      this.error = error instanceof Error ? error.message : String(error);
    } finally {
      this.foldManagementLoading = false;
    }
  }

  public async next(): Promise<void> {
    if (this.navLoading) return;
    if (this.currentIndex >= this.items.length - 1 && (!this.hasMore || !this.loadMore)) return;
    await this.runWithUnsavedChangesGuard(async () => {
      if (this.currentIndex < this.items.length - 1) {
        this._clearMedia();
        this.currentIndex += 1;
        this.syncFromCurrent(false);
        return;
      }
      // Reached the last currently-loaded item — pull in more from the host (same
      // pagination its infinite scroll uses) before advancing, instead of wrapping around.
      if (!this.hasMore || !this.loadMore) return;
      this.navLoading = true;
      try {
        await this.loadMore();
        // The host updates the bindable item array after its request completes. Wait for one
        // browser frame before checking the length so the item at the page boundary is reachable.
        await new Promise<void>(resolve => requestAnimationFrame(() => resolve()));
      } finally {
        this.navLoading = false;
      }
      if (this.currentIndex < this.items.length - 1) {
        this._clearMedia();
        this.currentIndex += 1;
        this.syncFromCurrent(false);
      }
    });
  }

  private focusLightbox(): void {
    requestAnimationFrame(() => {
      if (this.currentItem && this.lightboxEl?.isConnected) this.lightboxEl.focus({ preventScroll: true });
    });
  }

  public async save(): Promise<void> {
    if (!this.currentItem || this.saving || !this.hasUnsavedChanges) return;
    this.saving = true;
    this.error = '';
    try {
      const updated = await updateMediaItem(this.currentItem.id, {
        title: this.title.trim(),
        description: this.description.trim(),
        capturedAt: this.capturedAt ? new Date(this.capturedAt).toISOString() : null,
        tagNames: this.tags.map(tag => tag.name.trim()).filter(Boolean),
        isCapturedAtTimeAccurate: !this.notAccurateTimestamp,
        rotationDegrees: this.rotationDegrees,
      });
      this.replaceCurrentItem(updated);
      this.syncFields(updated);
      this.saved(updated);
    } catch (error) {
      this.error = error instanceof Error ? error.message : String(error);
    } finally {
      this.saving = false;
    }
  }

  // Marks (or unmarks) the current photo as "reviewed, no known persons to tag" so it can be
  // excluded from the gallery's "needs person review" filter without requiring a marker.
  // Uses the item's already-persisted fields (not the possibly-dirty form inputs) so this
  // toggle never accidentally saves unrelated in-progress edits.
  public togglingNoPersons = false;

  public async toggleNoPersonsToMark(): Promise<void> {
    if (!this.currentItem || this.isVideo || this.togglingNoPersons) return;
    this.setDetailTab('markers');
    this.togglingNoPersons = true;
    try {
      const updated = await updateMediaItem(this.currentItem.id, {
        title: this.currentItem.title,
        description: this.currentItem.description,
        capturedAt: this.currentItem.capturedAt,
        tagNames: this.currentItem.tags.map(tag => tag.name),
        isCapturedAtTimeAccurate: this.currentItem.isCapturedAtTimeAccurate,
        noPersonsToMark: !this.currentItem.noPersonsToMark,
      });
      this.replaceCurrentItem(updated);
      this.deactivateMarkerEditMode(); // "no persons to mark" ends the tagging session for this item
      this.saved(updated);
      this.personDataChanged?.();
    } catch (error) {
      await ConfirmDialog.alert(error instanceof Error ? error.message : String(error), this.i18n.tr('common.error'));
    } finally {
      this.togglingNoPersons = false;
    }
  }

  // Toggles the item between "private" (only the owner sees it) and "family" (visible to all
  // family members, see doc/copilot-notes.md sharing design) visibility.
  public togglingVisibility = false;

  public async toggleVisibility(): Promise<void> {
    if (!this.currentItem || this.togglingVisibility) return;
    this.togglingVisibility = true;
    try {
      const updated = await updateMediaItem(this.currentItem.id, {
        title: this.currentItem.title,
        description: this.currentItem.description,
        capturedAt: this.currentItem.capturedAt,
        tagNames: this.currentItem.tags.map(tag => tag.name),
        isCapturedAtTimeAccurate: this.currentItem.isCapturedAtTimeAccurate,
        visibility: this.currentItem.visibility === 'family' ? 'private' : 'family',
      });
      this.replaceCurrentItem(updated);
      this.saved(updated);
    } catch (error) {
      await ConfirmDialog.alert(error instanceof Error ? error.message : String(error), this.i18n.tr('common.error'));
    } finally {
      this.togglingVisibility = false;
    }
  }

  // Marks (or unmarks) the current photo as "needs a later revision pass" — for photos where the
  // persons aren't known/markable right now but should be tagged in a dedicated future session.
  // Mutually exclusive with noPersonsToMark on the backend (setting one clears the other).
  public togglingNeedsRevision = false;

  public async toggleNeedsPersonRevision(): Promise<void> {
    if (!this.currentItem || this.isVideo || this.togglingNeedsRevision) return;
    this.setDetailTab('markers');
    this.togglingNeedsRevision = true;
    try {
      const updated = await updateMediaItem(this.currentItem.id, {
        title: this.currentItem.title,
        description: this.currentItem.description,
        capturedAt: this.currentItem.capturedAt,
        tagNames: this.currentItem.tags.map(tag => tag.name),
        isCapturedAtTimeAccurate: this.currentItem.isCapturedAtTimeAccurate,
        needsPersonRevision: !this.currentItem.needsPersonRevision,
      });
      this.replaceCurrentItem(updated);
      this.saved(updated);
      this.personDataChanged?.();
    } catch (error) {
      await ConfirmDialog.alert(error instanceof Error ? error.message : String(error), this.i18n.tr('common.error'));
    } finally {
      this.togglingNeedsRevision = false;
    }
  }

  public async deleteCurrent(): Promise<void> {
    if (!this.currentItem || this.deleting) return;
    const confirmed = await ConfirmDialog.confirm(
      this.currentItem.title
        ? this.i18n.tr('gallery.lightbox.deleteConfirm', { title: this.currentItem.title })
        : this.i18n.tr('gallery.lightbox.deleteConfirmUntitled'),
      this.i18n.tr('gallery.lightbox.deleteTitle'),
    );
    if (!confirmed) return;

    this.deleting = true;
    try {
      await deleteMediaItem(this.currentItem.id);
      this.deleted(this.currentItem.id);
    } catch (error) {
      await ConfirmDialog.alert(error instanceof Error ? error.message : String(error), this.i18n.tr('common.error'));
    } finally {
      this.deleting = false;
    }
  }

  // ── Collection membership (add to any / remove from current) ─────────────

  public showCollectionPicker = false;

  public openCollectionPicker(): void {
    this.showCollectionPicker = true;
  }

  public onCollectionPickerClosed(): void {
    this.showCollectionPicker = false;
  }

  public async onCollectionPickerSelected(collection: CollectionRef): Promise<void> {
    this.showCollectionPicker = false;
    if (!this.currentItem) return;
    try {
      const added = await addMediaItemsToCollection(collection.id, [this.currentItem.id]);
      await this.collectionItemsAdded?.(added);
    } catch (e) {
      await ConfirmDialog.alert(e instanceof Error ? e.message : String(e), this.i18n.tr('common.error'));
    }
  }

  public removingFromCollection = false;

  public async removeFromCurrentCollection(): Promise<void> {
    if (!this.currentItem || !this.currentCollectionId || this.removingFromCollection) return;
    const confirmed = await ConfirmDialog.confirm(
      this.i18n.tr('collections.removeFromCollection.one'),
      this.i18n.tr('collections.removeFromCollection.title'),
    );
    if (!confirmed) return;

    this.removingFromCollection = true;
    try {
      const removedItemId = this.currentItem.id;
      await removeMediaItemsFromCollection(this.currentCollectionId, [removedItemId]);
      this.removedFromCollection?.(removedItemId);
    } catch (e) {
      await ConfirmDialog.alert(e instanceof Error ? e.message : String(e), this.i18n.tr('common.error'));
    } finally {
      this.removingFromCollection = false;
    }
  }

  // `resetMarkerEditMode` is false when navigating to the next/previous item so an
  // active "mark persons" mode carries over across items, letting the user tag several
  // photos in a row without re-toggling it each time. It stays true for a freshly opened
  // lightbox (binding/startIndexChanged) so the mode never starts on unexpectedly.
  private syncFromCurrent(resetMarkerEditMode = true): void {
    this._horizontalItemId = this.items[this.currentIndex]?.id ?? null;
    this._foldedLoadGeneration++;
    this.foldedItems = [];
    this.foldedIndex = 0;
    this.foldedLoading = false;
    this.currentItem = this.items[this.currentIndex] ?? null;
    this.markers = [];
    this.pendingFaces = [];
    this.selectedPendingFaceId = null;
    this.imageLoaded = false;
    if (resetMarkerEditMode) this.deactivateMarkerEditMode();
    this._cancelDraw();
    this._endFaceDrag();
    this._pendingFaceForPicker = null;
    if (this.currentItem) {
      this.syncFields(this.currentItem);
      if (!this.isVideo) void this._loadMarkers();
      if (this.currentItem.foldedImageCount > 0 || this.currentItem.foldedIntoMediaItemId != null)
        void this._loadFoldedGroup(this.currentItem.id);
    }
  }

  private async _loadFoldedGroup(mediaItemId: string): Promise<void> {
    const generation = ++this._foldedLoadGeneration;
    this.foldedLoading = true;
    try {
      const group = await getMediaFoldGroup(mediaItemId);
      if (generation !== this._foldedLoadGeneration || this._horizontalItemId !== mediaItemId)
        return;
      this.foldedItems = group.items;
      const currentIndex = this.foldedItems.findIndex(item => item.id === this.currentItem?.id);
      this.foldedIndex = currentIndex >= 0 ? currentIndex : 0;
    } catch {
      if (generation === this._foldedLoadGeneration) {
        this.foldedItems = [];
        this.foldedIndex = 0;
      }
    } finally {
      if (generation === this._foldedLoadGeneration)
        this.foldedLoading = false;
    }
  }

  private showFoldedItem(index: number): void {
    const item = this.foldedItems[index];
    if (!item) return;
    this._clearMedia();
    this.foldedIndex = index;
    this.currentItem = item;
    this.markers = [];
    this.pendingFaces = [];
    this.selectedPendingFaceId = null;
    this.imageLoaded = false;
    this._cancelDraw();
    this._endFaceDrag();
    this._pendingFaceForPicker = null;
    this.syncFields(item);
    if (!this.isVideo) void this._loadMarkers();
  }

  private applyFoldGroup(group: MediaFoldGroup): void {
    if (group.items.length === 0) return;

    const currentId = this.currentItem?.id;
    const nextItem = group.items.find(item => item.id === currentId) ?? group.representative;
    const itemChanged = nextItem.id !== currentId;

    if (itemChanged)
      this._clearMedia();

    this._foldedLoadGeneration++;
    this.foldedItems = group.items;
    this.foldedIndex = Math.max(0, group.items.findIndex(item => item.id === nextItem.id));
    this._horizontalItemId = group.representative.id;
    this.currentItem = nextItem;
    this.syncFields(nextItem);

    if (itemChanged) {
      this.markers = [];
      this.pendingFaces = [];
      this.selectedPendingFaceId = null;
      this.imageLoaded = false;
      this._cancelDraw();
      this._endFaceDrag();
      this._pendingFaceForPicker = null;
      if (!this.isVideo) void this._loadMarkers();
    }
  }

  private foldGroupSnapshot(): MediaFoldGroup | null {
    if (!this.hasFoldedGroup) return null;
    const representative = this.foldedItems.find(item => item.foldedIntoMediaItemId == null)
      ?? this.foldedItems[0];
    return representative ? { representative, items: [...this.foldedItems] } : null;
  }

  private showStandaloneItem(item: MediaItem): void {
    this._foldedLoadGeneration++;
    this.foldedItems = [];
    this.foldedIndex = 0;
    this.foldedLoading = false;
    this._horizontalItemId = item.id;
    this.currentItem = item;
    this.syncFields(item);
  }

  private replaceCurrentItem(updated: MediaItem): void {
    this.currentItem = updated;
    this.items = this.items.map(item => item.id === updated.id ? updated : item);
    this.foldedItems = this.foldedItems.map(item => item.id === updated.id ? updated : item);
  }

  private async _loadMarkers(): Promise<void> {
    if (!this.currentItem) return;
    const itemId = this.currentItem.id;
    this.markersLoading = true;
    try {
      const markers = await listPersonMarkers(itemId);
      if (this.currentItem?.id === itemId) this.markers = markers;
    } catch {
      this.markers = [];
    } finally {
      this.markersLoading = false;
    }
  }

  public toggleMarkerEditMode(): void {
    if (this.markerEditMode) {
      this.deactivateMarkerEditMode();
      return;
    }
    this.activateMarkerEditMode();
    this.markersVisible = true;
  }

  private activateMarkerEditMode(): void {
    if (!this.markerEditMode && this.activeDetailTab !== 'markers') {
      this._detailTabBeforeMarkers = this.activeDetailTab;
    }
    this.markerEditMode = true;
    this.setDetailTab('markers');
  }

  private deactivateMarkerEditMode(): void {
    const wasActive = this.markerEditMode;
    this.markerEditMode = false;
    this._endFaceDrag();
    this.pendingFaces = [];
    this.selectedPendingFaceId = null;
    if (wasActive) {
      this.setDetailTab(this._detailTabBeforeMarkers);
    }
  }

  public toggleMarkersVisible(): void {
    this.markersVisible = !this.markersVisible;
    localStorage.setItem(MARKERS_VISIBLE_KEY, String(this.markersVisible));
  }

  public toggleOverlayMenu(): void {
    this.showOverlayMenu = !this.showOverlayMenu;
  }

  public closeOverlayMenu(): void {
    this.showOverlayMenu = false;
  }

  public rotateRight(): void {
    if (!this.currentItem || this.saving) return;
    this.rotationDegrees = normalizeRotationDegrees(this.rotationDegrees + 90);
    this.videoStyle = rotationStyle(this.rotationDegrees);
    this.refreshUnsavedChanges();
    this.updateImageGeometry();
  }

  public toggleOverlayPersonMarkers(): void {
    this.setOverlayInfo('personMarkers', !this.overlayInfo.personMarkers);
  }

  public setOverlayInfo(field: keyof OverlayInfoOptions, value: boolean): void {
    this.overlayInfo = { ...this.overlayInfo, [field]: value };
    localStorage.setItem(OVERLAY_INFO_KEY, JSON.stringify(this.overlayInfo));
  }

  public clearOverlayInfo(): void {
    this.overlayInfo = { date: false, time: false, position: false, personMarkers: false };
    localStorage.setItem(OVERLAY_INFO_KEY, JSON.stringify(this.overlayInfo));
  }

  private _loadDetailTab(): DetailTab {
    const stored = localStorage.getItem(DETAIL_TAB_KEY);
    return stored === 'details' || stored === 'markers' ? stored : 'metadata';
  }

  private _loadDetailTabBeforeMarkers(): NonMarkerDetailTab {
    return localStorage.getItem(DETAIL_TAB_RETURN_KEY) === 'details' ? 'details' : 'metadata';
  }

  private _loadOverlayInfo(): OverlayInfoOptions {
    const defaults: OverlayInfoOptions = { date: false, time: false, position: false, personMarkers: false };
    try {
      const stored = localStorage.getItem(OVERLAY_INFO_KEY);
      if (stored) return { ...defaults, ...JSON.parse(stored) as Partial<OverlayInfoOptions> };
    } catch {
      // ignore malformed storage
    }
    return defaults;
  }

  public toggleTooltipSettings(): void {
    this.showTooltipSettings = !this.showTooltipSettings;
  }

  public setTooltipField(field: 'name' | 'nickName', value: boolean): void {
    this.tooltipFields = { ...this.tooltipFields, [field]: value };
    this._saveTooltipFields();
  }

  public setAgeMode(mode: AgeDisplayMode): void {
    this.tooltipFields = { ...this.tooltipFields, ageMode: mode };
    this._saveTooltipFields();
  }

  public onImageMouseDown(event: MouseEvent): void {
    if (!this.markerEditMode || !this.imageWrapEl || event.button !== 0) return;
    const target = event.target as HTMLElement;
    if (target.closest('.face-marker')) return; // let marker click handlers take over
    event.preventDefault();
    this._beginDraw(event.clientX, event.clientY);

    // Track the drag on `document` (not just the image element) so the rectangle
    // keeps updating and a real mouseup still finalizes it even if the cursor
    // briefly leaves the image bounds mid-drag (very easy to do when the
    // displayed image is smaller than the surrounding viewer chrome/padding).
    document.addEventListener('mousemove', this._onDocumentMouseMove);
    document.addEventListener('mouseup', this._onDocumentMouseUp);
  }

  // Swipe-to-navigate (e.g. iPad), so the arrows aren't the only way to move
  // between items. Horizontal swipes change the current representative in either
  // view; vertical swipes change folded siblings in either view.
  // Kept separate from the marker-drawing touch handlers below.
  private _swipeStartX = 0;
  private _swipeStartY = 0;
  private _swipeTracking = false;
  private static readonly SWIPE_MIN_DISTANCE = 50;

  public onViewerTouchStart(event: TouchEvent): void {
    if (this.markerEditMode
      || event.touches.length !== 1) {
      this._swipeTracking = false;
      return;
    }
    const touch = event.touches[0];
    this._swipeStartX = touch.clientX;
    this._swipeStartY = touch.clientY;
    this._swipeTracking = true;
  }

  public onViewerTouchEnd(event: TouchEvent): void {
    if (!this._swipeTracking) return;
    this._swipeTracking = false;
    const touch = event.changedTouches[0];
    if (!touch) return;
    const deltaX = touch.clientX - this._swipeStartX;
    const deltaY = touch.clientY - this._swipeStartY;
    const absX = Math.abs(deltaX);
    const absY = Math.abs(deltaY);

    if (absY >= absX) {
      if (absY < MediaLightbox.SWIPE_MIN_DISTANCE || !this.hasFoldedGroup) return;
      if (deltaY < 0) this.nextFolded();
      else this.prevFolded();
      return;
    }

    if (absX < MediaLightbox.SWIPE_MIN_DISTANCE) return;
    if (deltaX < 0) void this.next();
    else this.prev();
  }

  public onImageTouchStart(event: TouchEvent): void {
    if (!this.markerEditMode || !this.imageWrapEl) return;
    if (event.touches.length > 1) {
      // A second finger joined — this is a pinch-to-zoom gesture, not a marker
      // drag. Abandon any in-progress rectangle and don't preventDefault, so the
      // browser handles the native zoom instead of it being read as a drag.
      this._cancelDraw();
      return;
    }
    const target = event.target as HTMLElement;
    if (target.closest('.face-marker')) return; // let marker tap handlers take over
    const touch = event.touches[0];
    if (!touch) return;
    // Prevent the page behind the lightbox from scrolling while drawing with
    // touch/pencil input (iPad Safari otherwise treats this as a scroll gesture).
    event.preventDefault();
    this._beginDraw(touch.clientX, touch.clientY);

    document.addEventListener('touchmove', this._onDocumentTouchMove, { passive: false });
    document.addEventListener('touchend', this._onDocumentTouchEnd);
    document.addEventListener('touchcancel', this._onDocumentTouchEnd);
  }

  private _beginDraw(clientX: number, clientY: number): void {
    if (!this.imageWrapEl) return;
    const rect = this.imageWrapEl.getBoundingClientRect();
    this._drawStart = this._displayPointToOriginal(clientX, clientY, rect);
    this.isDrawing = true;
    this.drawRect = { left: this._drawStart.x * 100, top: this._drawStart.y * 100, width: 0, height: 0 };
  }

  private _updateDraw(clientX: number, clientY: number): void {
    if (!this.isDrawing || !this._drawStart || !this.imageWrapEl) return;
    const rect = this.imageWrapEl.getBoundingClientRect();
    const point = this._displayPointToOriginal(clientX, clientY, rect);
    const x = point.x;
    const y = point.y;
    const left = Math.min(x, this._drawStart.x);
    const top = Math.min(y, this._drawStart.y);
    const width = Math.abs(x - this._drawStart.x);
    const height = Math.abs(y - this._drawStart.y);
    this.drawRect = { left: left * 100, top: top * 100, width: width * 100, height: height * 100 };
  }

  private _finishDraw(): void {
    if (!this.isDrawing || !this.drawRect) { this._cancelDraw(); return; }
    const { left, top, width, height } = this.drawRect;
    this.isDrawing = false;
    if (width / 100 < MIN_MARKER_SIZE || height / 100 < MIN_MARKER_SIZE) {
      this.drawRect = null;
      return;
    }
    this._pendingRect = { x: left / 100, y: top / 100, width: width / 100, height: height / 100 };
    this._reassignMarker = null;
    this._pendingFaceForPicker = null;
    this.showPersonPicker = true;
  }

  private readonly _onDocumentMouseMove = (event: MouseEvent): void => {
    this._updateDraw(event.clientX, event.clientY);
  };

  private readonly _onDocumentMouseUp = (): void => {
    document.removeEventListener('mousemove', this._onDocumentMouseMove);
    document.removeEventListener('mouseup', this._onDocumentMouseUp);
    this._finishDraw();
  };

  private readonly _onDocumentTouchMove = (event: TouchEvent): void => {
    if (event.touches.length > 1) {
      // A second finger joined mid-drag — treat it as a pinch-zoom gesture and
      // abandon the marker rectangle instead of fighting the browser for it.
      this._cancelDraw();
      return;
    }
    const touch = event.touches[0];
    if (!touch) return;
    if (this.isDrawing) event.preventDefault(); // keep blocking page scroll for the whole drag
    this._updateDraw(touch.clientX, touch.clientY);
  };

  private readonly _onDocumentTouchEnd = (): void => {
    document.removeEventListener('touchmove', this._onDocumentTouchMove);
    document.removeEventListener('touchend', this._onDocumentTouchEnd);
    document.removeEventListener('touchcancel', this._onDocumentTouchEnd);
    this._finishDraw();
  };

  private _cancelDraw(): void {
    document.removeEventListener('mousemove', this._onDocumentMouseMove);
    document.removeEventListener('mouseup', this._onDocumentMouseUp);
    document.removeEventListener('touchmove', this._onDocumentTouchMove);
    document.removeEventListener('touchend', this._onDocumentTouchEnd);
    document.removeEventListener('touchcancel', this._onDocumentTouchEnd);
    this.isDrawing = false;
    this.drawRect = null;
    this._drawStart = null;
  }

  private _displayPointToOriginal(
    clientX: number,
    clientY: number,
    rect: DOMRect,
  ): DrawPoint {
    const displayPoint = {
      x: Math.min(1, Math.max(0, (clientX - rect.left) / rect.width)),
      y: Math.min(1, Math.max(0, (clientY - rect.top) / rect.height)),
    };
    return displayPointToOriginal(displayPoint, this.rotationDegrees);
  }

  // --- Move/resize of the selected pending face -----------------------------------------
  // Mirrors the free-draw infrastructure above (normalized coords via imageWrapEl's rect), but
  // adjusts an *existing* PendingFace instead of drawing a new one, and always replaces
  // `pendingFaces` with a new array (never mutates a face in place) to stay consistent with this
  // codebase's array-replacement convention for reactive updates.
  //
  // IMPORTANT: pointer capture and the pointermove/up listeners are attached to `imageWrapEl`
  // (a stable container that's never recreated), NOT to the dragged marker/handle `<div>` itself.
  // Every drag update replaces `pendingFaces` with new objects, so Aurelia's `repeat.for`
  // destroys and recreates that exact marker/handle element on the very next render — which
  // silently releases pointer capture and detaches any listeners attached to it, permanently
  // stalling the drag after the first move. On iOS Safari this also appeared to wedge touch
  // tracking for the rest of the page (other buttons stopped responding) until reload — capturing
  // on a stable element avoids all of that.
  private _beginFaceDrag(
    mode: 'move' | 'resize',
    handle: ResizeHandle | null,
    face: PendingFace,
    clientX: number,
    clientY: number,
    pointerId: number,
  ): void {
    if (!this.imageWrapEl) return;
    const captureEl = this.imageWrapEl;
    const rect = captureEl.getBoundingClientRect();
    this._dragFaceId = face.clientId;
    this._dragMode = mode;
    this._dragHandle = handle;
    this._dragStartPoint = {
      ...this._displayPointToOriginal(clientX, clientY, rect),
    };
    this._dragStartRect = { x: face.x, y: face.y, width: face.width, height: face.height };
    this._dragPointerId = pointerId;
    this._dragCaptureEl = captureEl;
    try {
      captureEl.setPointerCapture(pointerId);
    } catch {
      // Some environments (older browsers, certain synthetic events) may not support pointer
      // capture on this element — dragging still works via the pointermove/up listeners below,
      // just without the "keeps tracking outside the element bounds" guarantee.
    }
    captureEl.addEventListener('pointermove', this._onFaceDragPointerMove);
    captureEl.addEventListener('pointerup', this._onFaceDragPointerUp);
    captureEl.addEventListener('pointercancel', this._onFaceDragPointerUp);
  }

  private _updateFaceDrag(clientX: number, clientY: number): void {
    if (!this._dragFaceId || !this._dragStartPoint || !this._dragStartRect || !this.imageWrapEl) return;
    const rect = this.imageWrapEl.getBoundingClientRect();
    const point = this._displayPointToOriginal(clientX, clientY, rect);
    const deltaX = point.x - this._dragStartPoint.x;
    const deltaY = point.y - this._dragStartPoint.y;
    const updated =
      this._dragMode === 'resize' && this._dragHandle
        ? this._computeResizedFaceRect(this._dragStartRect, this._dragHandle, deltaX, deltaY)
        : this._computeMovedFaceRect(this._dragStartRect, deltaX, deltaY);
    const dragFaceId = this._dragFaceId;
    this.pendingFaces = this.pendingFaces.map((face) =>
      face.clientId === dragFaceId ? { ...face, ...updated } : face,
    );
  }

  private _computeMovedFaceRect(start: FaceRect, deltaX: number, deltaY: number): FaceRect {
    const x = Math.min(Math.max(0, start.x + deltaX), 1 - start.width);
    const y = Math.min(Math.max(0, start.y + deltaY), 1 - start.height);
    return { x, y, width: start.width, height: start.height };
  }

  private _computeResizedFaceRect(
    start: FaceRect,
    handle: ResizeHandle,
    deltaX: number,
    deltaY: number,
  ): FaceRect {
    let left = start.x;
    let top = start.y;
    let right = start.x + start.width;
    let bottom = start.y + start.height;
    if (handle === 'nw' || handle === 'sw') left = Math.min(Math.max(0, start.x + deltaX), right - MIN_MARKER_SIZE);
    if (handle === 'ne' || handle === 'se') right = Math.max(Math.min(1, right + deltaX), left + MIN_MARKER_SIZE);
    if (handle === 'nw' || handle === 'ne') top = Math.min(Math.max(0, start.y + deltaY), bottom - MIN_MARKER_SIZE);
    if (handle === 'sw' || handle === 'se') bottom = Math.max(Math.min(1, bottom + deltaY), top + MIN_MARKER_SIZE);
    return { x: left, y: top, width: right - left, height: bottom - top };
  }

  private _endFaceDrag(): void {
    const draggedFaceId = this._dragFaceId;
    const startRect = this._dragStartRect;
    if (this._dragCaptureEl) {
      this._dragCaptureEl.removeEventListener('pointermove', this._onFaceDragPointerMove);
      this._dragCaptureEl.removeEventListener('pointerup', this._onFaceDragPointerUp);
      this._dragCaptureEl.removeEventListener('pointercancel', this._onFaceDragPointerUp);
      if (this._dragPointerId !== null && this._dragCaptureEl.hasPointerCapture?.(this._dragPointerId)) {
        try {
          this._dragCaptureEl.releasePointerCapture(this._dragPointerId);
        } catch {
          // already released (e.g. pointercancel) — nothing to do
        }
      }
    }
    this._dragFaceId = null;
    this._dragMode = null;
    this._dragHandle = null;
    this._dragStartPoint = null;
    this._dragStartRect = null;
    this._dragPointerId = null;
    this._dragCaptureEl = null;

    // The box moved/resized during this drag (as opposed to e.g. a click that didn't actually
    // drag) — its previously computed suggestions no longer necessarily reflect the face under
    // the (new) rectangle, so recompute them for wherever it ended up.
    if (draggedFaceId && startRect) {
      const face = this.pendingFaces.find(f => f.clientId === draggedFaceId);
      if (face && (face.x !== startRect.x || face.y !== startRect.y || face.width !== startRect.width || face.height !== startRect.height)) {
        void this._refreshSuggestionsForFace(draggedFaceId);
      }
    }
  }

  // Re-runs the "who might this be" comparison for a single pending face against its current
  // (post-drag) rectangle, replacing its (now possibly stale) suggestions in place. Best-effort:
  // a failure here just leaves the previous suggestions showing rather than blocking the drag UX.
  private async _refreshSuggestionsForFace(faceId: string): Promise<void> {
    if (!this.currentItem) return;
    const itemId = this.currentItem.id;
    const face = this.pendingFaces.find(f => f.clientId === faceId);
    if (!face) return;
    try {
      const suggestions = await suggestPersonsForRect(itemId, {
        x: face.x, y: face.y, width: face.width, height: face.height,
      });
      if (this.currentItem?.id !== itemId) return;
      this.pendingFaces = this.pendingFaces.map(f => f.clientId === faceId ? { ...f, suggestions } : f);
    } catch {
      // Keep the previous suggestions rather than surfacing an error for a background refresh.
    }
  }

  private readonly _onFaceDragPointerMove = (event: PointerEvent): void => {
    if (event.pointerId !== this._dragPointerId) return;
    event.preventDefault();
    this._updateFaceDrag(event.clientX, event.clientY);
  };

  private readonly _onFaceDragPointerUp = (event: PointerEvent): void => {
    if (event.pointerId !== this._dragPointerId) return;
    this._endFaceDrag();
  };

  public reassignMarker(marker: PersonMarker): void {
    if (!this.markerEditMode) return;
    this.setDetailTab('markers');
    this._reassignMarker = marker;
    this._pendingRect = null;
    this._pendingFaceForPicker = null;
    this.showPersonPicker = true;
  }

  public async runFaceDetection(): Promise<void> {
    if (!this.currentItem || this.isVideo || this.detectingFaces) return;
    this.activateMarkerEditMode();
    const itemId = this.currentItem.id;
    this.detectingFaces = true;
    try {
      const faces = await detectFaces(itemId, {
        topMarginPercent: getFaceMarginTopPercent(),
        sideMarginPercent: getFaceMarginSidePercent(),
        bottomMarginPercent: getFaceMarginBottomPercent(),
        scoreThreshold: getFaceScoreThreshold(),
        maxWorkingDimension: getFaceMaxWorkingDimension(),
      });
      if (this.currentItem?.id !== itemId || !this.markerEditMode) return;
      const pendingFaces = faces
        .filter(face => !this.markers.some(marker => this._iou(face, marker) > 0.3))
        .map(face => ({
          clientId: crypto.randomUUID(),
          x: face.x,
          y: face.y,
          width: face.width,
          height: face.height,
          confidence: face.confidence,
          suggestions: face.suggestions,
        }));
      this.pendingFaces = pendingFaces;
      this.selectedPendingFaceId = pendingFaces[0]?.clientId ?? null;
      if (this.selectedPendingFaceId) {
        requestAnimationFrame(() => this._scrollPendingFaceIntoView(this.selectedPendingFaceId!));
      }
    } catch (error) {
      const message = error instanceof Error ? error.message : String(error);
      await ConfirmDialog.alert(
        `${this.i18n.tr('persons.marker.detectFacesError')}\n\n${message}`,
        this.i18n.tr('common.error'),
      );
    } finally {
      this.detectingFaces = false;
    }
  }

  public async onPersonPicked(person: Person): Promise<void> {
    this.showPersonPicker = false;
    if (!this.currentItem) return;
    try {
      if (this._reassignMarker) {
        const m = this._reassignMarker;
        const updated = await updatePersonMarker(m.id, { personId: person.id, x: m.x, y: m.y, width: m.width, height: m.height });
        this.markers = this.markers.map(marker => marker.id === updated.id ? updated : marker);
      } else if (this._pendingFaceForPicker) {
        const pendingFace = this._pendingFaceForPicker;
        const created = await createPersonMarker(this.currentItem.id, { personId: person.id, x: pendingFace.x, y: pendingFace.y, width: pendingFace.width, height: pendingFace.height });
        this._commitCreatedMarker(created);
        this._removePendingFace(pendingFace.clientId);
      } else if (this._pendingRect) {
        const created = await createPersonMarker(this.currentItem.id, { personId: person.id, ...this._pendingRect });
        this._commitCreatedMarker(created);
      }
    } catch (error) {
      await ConfirmDialog.alert(error instanceof Error ? error.message : String(error), this.i18n.tr('common.error'));
    } finally {
      this._pendingRect = null;
      this._reassignMarker = null;
      this._pendingFaceForPicker = null;
      this.drawRect = null;
    }
  }

  public onPersonPickerClosed(): void {
    this.showPersonPicker = false;
    this._pendingRect = null;
    this._reassignMarker = null;
    this._pendingFaceForPicker = null;
    this.drawRect = null;
  }

  public selectPendingFace(face: PendingFace): void {
    this.selectedPendingFaceId = face.clientId;
    this._scrollPendingFaceIntoView(face.clientId);
  }

  // Lets the user nudge (move) the currently selected pending face's box directly on the image,
  // so a slightly-off auto-detected box can be fine-tuned to better fit the face before assigning
  // a person — dragging anywhere on the marker body (not a resize handle) moves it. Uses Pointer
  // Events (not mouse/touch specific handlers) so mouse, touch and Apple Pencil/stylus input all
  // work uniformly — see the `_dragCaptureEl`/`_beginFaceDrag` comment for why.
  public onPendingFacePointerDown(face: PendingFace, event: PointerEvent): void {
    if (!event.isPrimary || event.button !== 0) return;
    event.stopPropagation();
    event.preventDefault();
    this.selectPendingFace(face);
    this._beginFaceDrag('move', null, face, event.clientX, event.clientY, event.pointerId);
  }

  // The four corner resize handles rendered only on the selected pending face (see the template).
  public onFaceResizeHandlePointerDown(face: PendingFace, handle: ResizeHandle, event: PointerEvent): void {
    if (!event.isPrimary || event.button !== 0) return;
    event.stopPropagation();
    event.preventDefault();
    this._beginFaceDrag('resize', handle, face, event.clientX, event.clientY, event.pointerId);
  }

  public pickPersonForFace(face: PendingFace, event?: Event): void {
    event?.stopPropagation();
    this._pendingRect = null;
    this._reassignMarker = null;
    this._pendingFaceForPicker = face;
    this.selectedPendingFaceId = face.clientId;
    this.showPersonPicker = true;
  }

  public async acceptSuggestion(face: PendingFace, suggestion: PersonSuggestion, event?: Event): Promise<void> {
    event?.stopPropagation();
    if (!this.currentItem) return;
    try {
      const created = await createPersonMarker(this.currentItem.id, {
        personId: suggestion.personId,
        x: face.x,
        y: face.y,
        width: face.width,
        height: face.height,
      });
      this._commitCreatedMarker(created);
      this._removePendingFace(face.clientId);
    } catch (error) {
      await ConfirmDialog.alert(error instanceof Error ? error.message : String(error), this.i18n.tr('common.error'));
    }
  }

  public discardPendingFace(face: PendingFace, event?: Event): void {
    event?.stopPropagation();
    this._removePendingFace(face.clientId);
  }

  public pendingFaceConfidenceLabel(face: PendingFace): string {
    return `${this.i18n.tr('persons.marker.pendingFaceConfidence')}: ${this._formatPercent(face.confidence)}`;
  }

  public suggestionLabel(suggestion: PersonSuggestion): string {
    return `${formatPersonName({
      firstName: suggestion.personFirstName,
      lastName: suggestion.personLastName,
      nickName: suggestion.personNickName,
    })} · ${this._formatPercent(suggestion.score)}`;
  }

  public faceCropStyle(face: FaceRect): string {
    const cropRect = this._getFaceCropRect(face);
    return `width: ${100 / cropRect.width}%; height: ${100 / cropRect.height}%; left: -${(cropRect.x / cropRect.width) * 100}%; top: -${(cropRect.y / cropRect.height) * 100}%; position: absolute; max-width: none; max-height: none;`;
  }

  // The preview box itself can't just be a fixed square — that would force faceCropStyle()'s
  // <img> (sized/positioned purely as percentages of this box) to stretch non-uniformly whenever
  // the crop rect's real pixel aspect ratio isn't 1:1 (which is the common case, especially with
  // the person-marker margin settings). Sizing the box to match that real aspect ratio (capped to
  // maxSizePx on the longer side) keeps the percentage math in faceCropStyle() distortion-free.
  public facePreviewContainerStyle(face: FaceRect, selected: boolean): string {
    const maxSizePx = selected ? 112 : 72;
    const cropRect = this._getFaceCropRect(face);
    const naturalWidth = this.imgEl?.naturalWidth ?? 0;
    const naturalHeight = this.imgEl?.naturalHeight ?? 0;

    let aspect = 1;
    if (naturalWidth > 0 && naturalHeight > 0 && cropRect.width > 0 && cropRect.height > 0) {
      const cropWidthPx = cropRect.width * naturalWidth;
      const cropHeightPx = cropRect.height * naturalHeight;
      if (cropWidthPx > 0 && cropHeightPx > 0) aspect = cropWidthPx / cropHeightPx;
    }

    const width = aspect >= 1 ? maxSizePx : Math.round(maxSizePx * aspect);
    const height = aspect >= 1 ? Math.round(maxSizePx / aspect) : maxSizePx;
    const quarterTurn = this.rotationDegrees === 90 || this.rotationDegrees === 270;
    return `width: ${quarterTurn ? height : width}px; height: ${quarterTurn ? width : height}px;`;
  }

  public facePreviewSurfaceStyle(face: FaceRect, selected: boolean): string {
    const maxSizePx = selected ? 112 : 72;
    const cropRect = this._getFaceCropRect(face);
    return facePreviewSurfaceStyle(
      cropRect,
      this.imgEl?.naturalWidth ?? 0,
      this.imgEl?.naturalHeight ?? 0,
      maxSizePx,
      this.rotationDegrees,
    );
  }

  public async deleteMarker(marker: PersonMarker, event: Event): Promise<void> {
    event.stopPropagation();
    const confirmed = await ConfirmDialog.confirm(
      this.i18n.tr('persons.marker.deleteConfirm', { name: formatPersonName({ firstName: marker.personFirstName, lastName: marker.personLastName, nickName: marker.personNickName }) }),
      this.i18n.tr('persons.marker.deleteTitle'),
    );
    if (!confirmed) return;
    try {
      await deletePersonMarker(marker.id);
      this.markers = this.markers.filter(m => m.id !== marker.id);
      this.personDataChanged?.();
    } catch (error) {
      await ConfirmDialog.alert(error instanceof Error ? error.message : String(error), this.i18n.tr('common.error'));
    }
  }

  public markerTooltip(marker: PersonMarker): string {
    const parts: string[] = [];
    if (this.tooltipFields.name) {
      const fullName = `${marker.personFirstName} ${marker.personLastName}`.trim();
      if (fullName) parts.push(fullName); // skip when blank so no stray leading " · " before the nickname
    }
    if (this.tooltipFields.nickName && marker.personNickName) parts.push(marker.personNickName);
    if (this.tooltipFields.ageMode !== 'off') {
      const ageText = this._formatAge(marker);
      if (ageText) parts.push(ageText);
    }
    if (parts.length > 0) return parts.join(' · ');
    return `${marker.personFirstName} ${marker.personLastName}`.trim() || marker.personNickName || '';
  }

  private _formatAge(marker: PersonMarker): string | null {
    const details = calculateAgeDetailsAtCapture(
      { birthYear: marker.personBirthYear, birthMonth: marker.personBirthMonth, birthDay: marker.personBirthDay },
      this.currentItem?.capturedAt,
    );
    if (!details) return null;
    const { years, months } = details;

    const showMonths = this.tooltipFields.ageMode === 'yearsAndMonths'
      || (this.tooltipFields.ageMode === 'bySetting' && years < getAgeAutoThresholdYears());

    if (!showMonths) return this.i18n.tr('persons.marker.ageYears', { count: years });

    const yearsPart = this.i18n.tr('persons.marker.ageYears', { count: years });
    const monthsPart = this.i18n.tr('persons.marker.ageMonths', { count: months });
    if (years === 0) return monthsPart;
    if (months === 0) return yearsPart;
    return `${yearsPart}, ${monthsPart}`;
  }

  private _loadTooltipFields(): MarkerTooltipFieldOptions {
    const defaults: MarkerTooltipFieldOptions = { name: true, nickName: false, ageMode: 'bySetting' };
    try {
      const stored = localStorage.getItem(TOOLTIP_FIELDS_KEY);
      if (stored) {
        const parsed = JSON.parse(stored) as Partial<MarkerTooltipFieldOptions> & {
          age?: boolean;
          ageAutoThresholdYears?: number;
          version?: number;
        };
        // Migrate the old boolean `age` field (pre-dating the age-mode feature) to `ageMode`.
        if (parsed.ageMode == null && typeof parsed.age === 'boolean') {
          parsed.ageMode = parsed.age ? 'bySetting' : 'off';
        } else if (parsed.version !== TOOLTIP_FIELDS_VERSION && parsed.ageMode === 'yearsOnly') {
          // The original age-mode default was yearsOnly. Treat that legacy default as
          // threshold-driven, while preserving explicit Years only choices from v2 onward.
          parsed.ageMode = 'bySetting';
        }
        delete (parsed as { age?: boolean }).age;
        delete parsed.ageAutoThresholdYears; // now a global setting, see person-age-settings.service.ts
        delete parsed.version;
        return { ...defaults, ...parsed };
      }
    } catch {
      // ignore malformed storage
    }
    return defaults;
  }

  private _saveTooltipFields(): void {
    localStorage.setItem(
      TOOLTIP_FIELDS_KEY,
      JSON.stringify({ ...this.tooltipFields, version: TOOLTIP_FIELDS_VERSION }),
    );
  }

  private _commitCreatedMarker(created: PersonMarker): void {
    this.markers = [...this.markers, created];
    // The backend automatically clears NoPersonsToMark once a marker exists (but leaves
    // NeedsPersonRevision alone — you can know some persons already and still need to
    // revisit the photo later for the rest); mirror that locally so the toggle
    // button/hint reflect it immediately without a refetch.
    if (this.currentItem?.noPersonsToMark) {
      this.replaceCurrentItem({ ...this.currentItem, noPersonsToMark: false });
    }
    this.personDataChanged?.();
  }

  private _removePendingFace(clientId: string): void {
    this.pendingFaces = this.pendingFaces.filter(face => face.clientId !== clientId);
    if (this.selectedPendingFaceId !== clientId) return;
    this.selectedPendingFaceId = this.pendingFaces[0]?.clientId ?? null;
  }

  private _scrollPendingFaceIntoView(clientId: string): void {
    const row = this.pendingFacesListEl?.querySelector<HTMLElement>(`[data-pending-face-id="${clientId}"]`);
    row?.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
  }

  private _getFaceCropRect(face: FaceRect): FaceRect {
    if (this._shouldTightlyZoomFace(face)) return face;
    const padding = 0.18;
    const x = Math.max(0, face.x - face.width * padding);
    const y = Math.max(0, face.y - face.height * padding);
    const right = Math.min(1, face.x + face.width * (1 + padding));
    const bottom = Math.min(1, face.y + face.height * (1 + padding));
    return {
      x,
      y,
      width: Math.max(0.0001, right - x),
      height: Math.max(0.0001, bottom - y),
    };
  }

  private _shouldTightlyZoomFace(face: FaceRect): boolean {
    const naturalWidth = this.imgEl?.naturalWidth ?? 0;
    const naturalHeight = this.imgEl?.naturalHeight ?? 0;
    if (naturalWidth <= 0 || naturalHeight <= 0) return true;
    return face.width * naturalWidth < this.faceZoomThresholdPx
      || face.height * naturalHeight < this.faceZoomThresholdPx;
  }

  private _formatPercent(value: number): string {
    return `${Math.round(value * 100)}%`;
  }

  private _iou(a: FaceRect, b: FaceRect): number {
    const left = Math.max(a.x, b.x);
    const top = Math.max(a.y, b.y);
    const right = Math.min(a.x + a.width, b.x + b.width);
    const bottom = Math.min(a.y + a.height, b.y + b.height);
    const intersectionWidth = Math.max(0, right - left);
    const intersectionHeight = Math.max(0, bottom - top);
    const intersection = intersectionWidth * intersectionHeight;
    if (intersection <= 0) return 0;
    const union = a.width * a.height + b.width * b.height - intersection;
    return union > 0 ? intersection / union : 0;
  }

  private _clearMedia(): void {
    // Imperative + synchronous, so the old media is gone before the DOM is
    // (re)attached/painted, regardless of Aurelia's queued binding updates.
    if (this.imgEl) {
      this.imgEl.removeAttribute('src');
    }
    if (this.videoEl) {
      this.videoEl.pause();
      this.videoEl.removeAttribute('src');
      this.videoEl.load();
    }
    this.imageLoaded = false;
    this.imageWrapStyle = '';
    this.imageSurfaceStyle = '';
    this.videoStyle = '';
  }

  private syncFields(item: MediaItem): void {
    this._syncingFields = true;
    try {
      this.title = item.title;
      this.description = item.description;
      this.capturedAt = item.capturedAt ?? '';
      this.notAccurateTimestamp = !item.isCapturedAtTimeAccurate;
      this.rotationDegrees = normalizeRotationDegrees(item.rotationDegrees);
      this.videoStyle = rotationStyle(this.rotationDegrees);
      this.tags = item.tags.map(tag => ({
        id: tag.id,
        name: tag.name,
        color: tag.color,
        groupPosition: 0,
        position: 0,
      }));
      this._editBaseline = this.captureEditState();
      this.hasUnsavedChanges = false;
      this._updateDisplayUrl();
    } finally {
      this._syncingFields = false;
    }
  }

  public refreshUnsavedChanges(tagsOverride?: Tag[]): void {
    if (this._syncingFields || !this._editBaseline) return;
    this.hasUnsavedChanges = !this.editStatesEqual(this._editBaseline, this.captureEditState(tagsOverride));
  }

  private captureEditState(tags: Tag[] = this.tags): MediaEditState {
    return {
      title: this.title.trim(),
      description: this.description.trim(),
      capturedAt: this.capturedAt,
      notAccurateTimestamp: this.notAccurateTimestamp,
      tagNames: tags
        .map(tag => tag.name.trim())
        .filter(Boolean)
        .sort(),
      rotationDegrees: this.rotationDegrees,
    };
  }

  private editStatesEqual(left: MediaEditState, right: MediaEditState): boolean {
    return left.title === right.title
      && left.description === right.description
      && left.capturedAt === right.capturedAt
      && left.notAccurateTimestamp === right.notAccurateTimestamp
      && left.tagNames.length === right.tagNames.length
      && left.tagNames.every((tagName, index) => tagName === right.tagNames[index])
      && left.rotationDegrees === right.rotationDegrees;
  }

  private async runWithUnsavedChangesGuard(action: () => void | Promise<void>): Promise<void> {
    if (this._leaveInProgress) return;
    this._leaveInProgress = true;
    try {
      if (this.hasUnsavedChanges) {
        const confirmed = await ConfirmDialog.confirm(
          this.i18n.tr('gallery.lightbox.unsavedChangesConfirm'),
          this.i18n.tr('gallery.lightbox.unsavedChangesTitle'),
        );
        if (!confirmed) return;
      }
      await action();
    } finally {
      this._leaveInProgress = false;
    }
  }

  private _updateDisplayUrl(): void {
    if (!this.currentItem) {
      this.displayUrl = '#';
      return;
    }

    const useMedium = !this.isVideo
      && this.mediaDisplayQuality === 'medium'
      && this._mediumFallbackItemId !== this.currentItem.id;
    this.displayUrl = useMedium
      ? getMediaMediumPreviewUrl(this.currentItem.id, this.currentItem.lastUpdatedAt)
      : this.downloadUrl;
  }

  private updateImageGeometry(): void {
    if (!this.viewerEl || !this.imgEl || !this.imageLoaded || !this.imgEl.isConnected) {
      this.imageWrapStyle = '';
      this.imageSurfaceStyle = '';
      return;
    }

    const naturalWidth = this.imgEl.naturalWidth || this.currentItem?.widthPx || 0;
    const naturalHeight = this.imgEl.naturalHeight || this.currentItem?.heightPx || 0;
    if (naturalWidth <= 0 || naturalHeight <= 0) return;

    const computed = getComputedStyle(this.viewerEl);
    const paddingX = parseFloat(computed.paddingLeft) + parseFloat(computed.paddingRight);
    const paddingY = parseFloat(computed.paddingTop) + parseFloat(computed.paddingBottom);
    const availableWidth = Math.max(1, this.viewerEl.clientWidth - paddingX);
    const availableHeight = Math.max(1, this.viewerEl.clientHeight - paddingY);
    const quarterTurn = this.rotationDegrees === 90 || this.rotationDegrees === 270;
    const displayAspect = quarterTurn
      ? naturalHeight / naturalWidth
      : naturalWidth / naturalHeight;
    const displayWidth = Math.min(availableWidth, availableHeight * displayAspect);
    const displayHeight = displayWidth / displayAspect;
    const sourceWidth = quarterTurn ? displayHeight : displayWidth;
    const sourceHeight = quarterTurn ? displayWidth : displayHeight;

    this.imageWrapStyle = `width: ${displayWidth}px; height: ${displayHeight}px;`;
    this.imageSurfaceStyle = [
      `width: ${sourceWidth}px`,
      `height: ${sourceHeight}px`,
      `transform: translate(-50%, -50%) rotate(${this.rotationDegrees}deg)`,
    ].join('; ') + ';';
  }
}
